import React from 'react';
import { useFlightProvider } from '@src/features/order/provider';
import { ArcCellInfo, ArcStack, ArcText } from '@src/core/ui';
import { arcSecToMin, arcSpatium, arcVelocitas } from '@src/core/extensions';
import { useUnitType } from '@src/core/features/settings';
import { EMPTY_STRING } from '@src/core/config';
import { FlightDataProps } from './types';
import { FlightEntity } from '@src/features/order/entities';

const cellInfoWidth = 'calc(50% - 8px)';

const FlightData: React.FC<FlightDataProps> = ({ droneSerial }) => {
  const { flightData, velocityNed, distanceSensor } = useFlightProvider();
  useUnitType();
  
  // Store previous flight data by serialNumber to prevent flickering
  const [previousFlightData, setPreviousFlightData] = React.useState<Record<string, FlightEntity>>({});
  
  // Update previous flight data when flightData is received for any drone
  React.useEffect(() => {
    if (!flightData?.serialNumber) return;
    const serial = flightData.serialNumber;
    setPreviousFlightData((prev) => {
      const existing = prev[serial];
      if (
        existing &&
        existing.latitude === flightData.latitude &&
        existing.longitude === flightData.longitude &&
        existing.altitude === flightData.altitude &&
        existing.speed === flightData.speed &&
        existing.distance === flightData.distance &&
        existing.totalDistance === flightData.totalDistance &&
        existing.estimatedFlightTime === flightData.estimatedFlightTime &&
        existing.missionProgress === flightData.missionProgress
      ) {
        return prev;
      }
      return { ...prev, [serial]: { ...flightData } };
    });
  }, [flightData]);
  
  // Get flight data for current drone: use current if available, otherwise use previous
  const displayedFlightData = React.useMemo(() => {
    if (!droneSerial) return flightData;
    
    // If flightData is for current drone, use it
    if (flightData?.serialNumber === droneSerial) {
      return flightData;
    }
    
    // Otherwise, use previous data for this drone (don't use flightData from other drones)
    return previousFlightData[droneSerial] ?? null;
  }, [flightData, droneSerial, previousFlightData]);

  return (
    <ArcStack
      height={'240px'}
      p={2.5}
      py={3}
      sx={{
        borderRadius: '4px',
        border: (theme) => `0.5px solid ${theme.palette.border.muted}`,
      }}
    >
      <ArcStack gap={2} direction='row' flexWrap='wrap'>
        <ArcCellInfo 
          label='V - SPEED' 
          width={cellInfoWidth}> 
          {arcVelocitas(velocityNed?.down_m_s ? -1*(velocityNed?.down_m_s) : undefined, { fromSi: true })}
        </ArcCellInfo>
        <ArcCellInfo 
          label='H - SPEED' 
          width={cellInfoWidth}> 
          {arcVelocitas(displayedFlightData?.speed, { fromSi: true })}
        </ArcCellInfo>

        <ArcCellInfo 
          label='TOTAL DIST' 
          width={cellInfoWidth}> 
          {arcSpatium(displayedFlightData?.totalDistance, { fromSi: true })}
        </ArcCellInfo>
        <ArcCellInfo 
          label='DIST FROM HOME' 
          width={cellInfoWidth}> 
          {arcSpatium(displayedFlightData?.distance, { fromSi: true })}
        </ArcCellInfo>

        <ArcCellInfo 
          label='FLIGHT TIME' 
          width={cellInfoWidth}> 
          {arcSecToMin(displayedFlightData?.estimatedFlightTime)}
        </ArcCellInfo>

        <ArcCellInfo 
          label='ETA' 
          width={cellInfoWidth}> 
          {EMPTY_STRING}
        </ArcCellInfo>

        <ArcCellInfo 
          label='HOME ALT' 
          width={cellInfoWidth}> 
          {arcSpatium(displayedFlightData?.altitude, { fromSi: true })}
        </ArcCellInfo>

        <ArcCellInfo 
          label='AGL' 
          width={cellInfoWidth}> 
          <ArcText.h6 fontWeight={700}> {arcSpatium(distanceSensor?.current_distance_m, { fromSi: true })} </ArcText.h6>
        </ArcCellInfo>
         
      </ArcStack>
    </ArcStack>
  );
};

export default FlightData;